import { useEffect, useRef, useState } from "react";
import { Camera, ShieldCheck, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Seo } from "@/components/site/Seo";
import { Field, Honeypot, ConsentBox, SubmitBtn, SuccessBox } from "@/components/site/FormBits";

const INTRO = "26 Ekim 2026 tarihinde iptal edilen Aydınfest konseri nedeniyle görev yerine gelen özel güvenlik personelimizin bilgilerinin alınması, görev yerine geliş durumlarının fotoğraflar üzerinden değerlendirilmesi ve destek ödemelerinin düzenli şekilde takip edilmesi amacıyla hazırlanmıştır. Lütfen aşağıdaki bilgileri eksiksiz doldurarak konser alanında çektiğiniz fotoğrafı yükleyiniz.";
const CLOSED = "Bilgi toplama süreci sona ermiştir.";
const EMPTY = { name: "", phone: "", iban: "TR", kvkk_ack: false, iban_owner_ack: false, truth_ack: false, explicit_consent: false, website: "" };
const MAX = 5 * 1024 * 1024;

export const phoneOk = (v) => /^5\d{9}$/.test((v || "").replace(/\D/g, "").replace(/^90(?=\d{10}$)/, "").replace(/^0/, ""));

export function ibanOk(v) {
  const s = (v || "").replace(/\s/g, "").toUpperCase();
  if (!/^TR\d{24}$/.test(s)) return false;
  const num = (s.slice(4) + s.slice(0, 4)).replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  return [...num].reduce((r, d) => (r * 10 + Number(d)) % 97, 0) === 1;
}

const fmtIban = (v) => {
  const s = v.replace(/\s/g, "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 26);
  return s.replace(/(.{4})/g, "$1 ").trim();
};

function validate(v, photo) {
  const checks = {
    name: !(v.name.trim().length >= 5 && v.name.trim().includes(" ")) && "Adınızı ve soyadınızı eksiksiz yazın.",
    phone: !phoneOk(v.phone) && "Geçerli bir cep telefonu numarası girin (örn. 0532 123 45 67).",
    iban: !ibanOk(v.iban) && "Geçerli bir IBAN girin (TR ile başlayan 26 karakter).",
    photo: !photo && "Konser alanında çekilen fotoğrafınızı yükleyin.",
    kvkk_ack: !v.kvkk_ack && "Aydınlatma metnini okuduğunuzu onaylayın.",
    iban_owner_ack: !v.iban_owner_ack && "IBAN'ın kendi adınıza kayıtlı olduğunu onaylayın.",
    truth_ack: !v.truth_ack && "Bilgilerin doğruluğunu onaylayın.",
  };
  return Object.fromEntries(Object.entries(checks).filter(([, m]) => m));
}

function PhotoPicker({ photo, setPhoto, error, setErr }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!photo) { setUrl(""); return undefined; }
    const u = URL.createObjectURL(photo);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [photo]);
  const pick = (f) => {
    if (!f) return;
    if (!/\.(jpe?g|png)$/i.test(f.name)) return setErr("Yalnızca JPG, JPEG veya PNG fotoğraf yükleyebilirsiniz.");
    if (f.size > MAX) return setErr("Fotoğraf en fazla 5 MB olabilir.");
    setErr("");
    setPhoto(f);
  };
  return (
    <Field id="af-photo" label="Konser Alanında Çekilen Fotoğraf" required error={error} hint="Lütfen konser alanında çekilen fotoğrafınızı yükleyiniz. Fotoğraf, görev yerine geldiğinizin değerlendirilmesi amacıyla incelenecektir.">
      {url ? (
        <div className="relative inline-block" data-testid="af-photo-preview">
          <img src={url} alt="Seçilen fotoğraf" className="max-h-64 rounded-lg border border-zinc-200" />
          <button type="button" onClick={() => setPhoto(null)} className="absolute right-2 top-2 rounded-full bg-black/70 p-1 text-white" aria-label="Fotoğrafı kaldır" data-testid="af-photo-remove"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-zinc-300 bg-zinc-50 p-6 text-center text-sm text-zinc-600 hover:border-[color:var(--red)]">
          <Camera className="h-8 w-8 text-[color:var(--red)]" />
          <span className="font-semibold">Fotoğraf seç veya çek</span>
          <span className="text-xs text-zinc-500">JPG, JPEG veya PNG · en fazla 5 MB</span>
          <input id="af-photo" type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid="af-photo-input" />
        </label>
      )}
    </Field>
  );
}

function LegalText({ legal }) {
  return (
    <details className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700" data-testid="af-kvkk-text">
      <summary className="cursor-pointer font-semibold text-zinc-900">Aydınfest 2026 Kişisel Verilerin İşlenmesine İlişkin Aydınlatma Metni</summary>
      <div className="mt-3 space-y-2">
        <p><b>Veri sorumlusu:</b> {legal}.</p>
        <p><b>İşlenen veriler:</b> ad soyad, telefon numarası, IBAN numarası, konser alanında çekilen fotoğraf, başvuru tarih-saati ile inceleme ve ödeme kayıtları.</p>
        <p><b>Amaç:</b> 26 Ekim 2026 tarihli Aydınfest konserine görev için gelen personelin tespit edilmesi, görev yerine gelişin fotoğraf üzerinden değerlendirilmesi ve destek ödemesinin yapılıp takip edilmesi. Veriler başka bir amaçla kullanılmaz.</p>
        <p><b>Hukuki sebep:</b> 6698 sayılı KVKK md. 5/2-c (bir sözleşmenin ifasıyla doğrudan ilgili olması) ve 5/2-f (veri sorumlusunun meşru menfaati).</p>
        <p><b>Aktarım:</b> Veriler, ödemenin yapılması için yalnızca ilgili banka ile paylaşılır; yurt dışına aktarılmaz. Kayıtlar yetkili yöneticilerin erişebildiği korumalı sistemde saklanır.</p>
        <p><b>Saklama:</b> Veriler, destek ödemesi ve olası itirazlar sonuçlanana kadar saklanır; sonrasında yetkili yönetici tarafından kalıcı olarak silinir.</p>
        <p><b>Haklarınız:</b> KVKK md. 11 kapsamındaki taleplerinizi şirketimizin iletişim kanallarından iletebilirsiniz.</p>
      </div>
    </details>
  );
}

const CONSENT_TEXT = "Gelecekte düzenlenecek etkinliklerdeki görev fırsatları için Rüzgar Özel Güvenlik'in telefon numaram üzerinden (arama, SMS veya WhatsApp) benimle iletişime geçmesine açık rıza veriyorum. (İsteğe bağlıdır; onay vermemeniz başvurunuzu etkilemez.)";

function Consents({ v, set, err }) {
  return (
    <div className="space-y-3">
      <ConsentBox id="af-kvkk" checked={v.kvkk_ack} onChange={set("kvkk_ack")} error={err.kvkk_ack} rest="Yukarıdaki Aydınlatma Metni'ni okudum ve anladım. *" />
      <ConsentBox id="af-iban-owner" checked={v.iban_owner_ack} onChange={set("iban_owner_ack")} error={err.iban_owner_ack} rest="Bildirdiğim IBAN numarasının kendi adıma kayıtlı bir hesaba ait olduğunu beyan ederim. Başkasına ait IBAN'a ödeme yapılmaz. *" />
      <ConsentBox id="af-truth" checked={v.truth_ack} onChange={set("truth_ack")} error={err.truth_ack} rest="Verdiğim bilgilerin ve yüklediğim fotoğrafın doğru olduğunu, fotoğrafın 26 Ekim 2026 tarihinde konser alanında tarafımdan çekildiğini beyan ederim. *" />
      <div className="rounded-lg border border-zinc-200 p-4" data-testid="af-explicit-block">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Açık Rıza Metni (isteğe bağlı)</p>
        <ConsentBox id="af-explicit" checked={v.explicit_consent} onChange={set("explicit_consent")} rest={CONSENT_TEXT} />
      </div>
    </div>
  );
}

function useAfSubmit() {
  const token = useRef(crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [srvErr, setSrvErr] = useState("");
  const send = async (v, photo) => {
    setBusy(true);
    setSrvErr("");
    const fd = new FormData();
    ["name", "phone", "iban", "website"].forEach((k) => fd.append(k, v[k]));
    ["kvkk_ack", "iban_owner_ack", "truth_ack", "explicit_consent"].forEach((k) => fd.append(k, v[k] ? "1" : ""));
    fd.append("submit_token", token.current);
    fd.append("photo", photo);
    try {
      setDone((await api.post("/public/aydinfest/application", fd)).data);
    } catch (e) {
      setSrvErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, done, srvErr, send };
}

function Receipt({ done }) {
  const at = new Date(done.created_at).toLocaleString("tr-TR", { dateStyle: "long", timeStyle: "short" });
  return (
    <SuccessBox title="Bilgileriniz başarıyla alınmıştır. Teşekkür ederiz." text="Lütfen kayıt numaranızı saklayınız." testid="af-success">
      <p className="mt-4 text-lg font-extrabold text-[color:var(--red)]" data-testid="af-ref-no">Kayıt No: {done.ref_no}</p>
      <p className="text-sm text-zinc-600" data-testid="af-created-at">Başvuru tarihi: {at}</p>
    </SuccessBox>
  );
}

function AfForm() {
  const site = useSite();
  const [v, setV] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [err, setErr] = useState({});
  const { busy, done, srvErr, send } = useAfSubmit();
  const set = (k) => (val) => setV((p) => ({ ...p, [k]: val }));
  const inp = (k) => (e) => set(k)(k === "iban" ? fmtIban(e.target.value) : e.target.value);
  const submit = (e) => {
    e.preventDefault();
    const found = validate(v, photo);
    setErr(found);
    if (Object.keys(found).length === 0) send(v, photo);
  };
  if (done) return <Receipt done={done} />;
  return (
    <form onSubmit={submit} noValidate className="relative space-y-6" data-testid="af-form">
      <Honeypot value={v.website} onChange={set("website")} />
      <Field id="af-name" label="Ad Soyad" required error={err.name}><input id="af-name" value={v.name} onChange={inp("name")} className="field-input" autoComplete="name" maxLength={80} data-testid="af-name" /></Field>
      <Field id="af-phone" label="Telefon Numarası" required error={err.phone}><input id="af-phone" type="tel" inputMode="tel" value={v.phone} onChange={inp("phone")} className="field-input" placeholder="0532 123 45 67" autoComplete="tel" maxLength={20} data-testid="af-phone" /></Field>
      <Field id="af-iban" label="IBAN Numarası" required error={err.iban} hint="Kendi adınıza kayıtlı hesabın IBAN'ını yazın (TR ile başlayan 26 karakter).">
        <input id="af-iban" value={v.iban} onChange={inp("iban")} className="field-input font-mono tracking-wider" placeholder="TR00 0000 0000 0000 0000 0000 00" autoComplete="off" data-testid="af-iban" />
      </Field>
      <PhotoPicker photo={photo} setPhoto={setPhoto} error={err.photo} setErr={(m) => setErr((p) => ({ ...p, photo: m }))} />
      <LegalText legal={site?.legal_name || "Rüzgar Özel Güvenlik"} />
      <Consents v={v} set={set} err={err} />
      {srvErr && <p className="rounded-lg bg-red-50 p-4 text-sm font-semibold text-red-700" data-testid="af-server-error">{srvErr}</p>}
      <SubmitBtn loading={busy} testid="af-submit">BİLGİLERİMİ GÖNDER</SubmitBtn>
    </form>
  );
}

function Body() {
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/public/aydinfest/status").then((r) => setOpen(r.data.open)).catch(() => setOpen(true));
  }, []);
  if (open === null) return null;
  if (!open) return <p className="rounded-lg border border-zinc-200 bg-zinc-50 p-8 text-center text-lg font-bold text-zinc-800" data-testid="af-closed">{CLOSED}</p>;
  return <AfForm />;
}

export default function Aydinfest() {
  return (
    <div className="bg-zinc-100">
      <Seo title="Aydınfest 2026 Personel Bilgi ve Fotoğraf Formu | Rüzgar Özel Güvenlik" noindex path="/aydinfest-basvuru" />
      <section className="bg-[color:var(--ink)] py-12 text-white">
        <div className="mx-auto max-w-3xl px-4">
          <p className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--red)]"><ShieldCheck className="h-4 w-4" /> Aydınfest Başvuru</p>
          <h1 className="text-3xl font-extrabold leading-tight sm:text-4xl" data-testid="af-title">AYDINFEST 2026 – PERSONEL BİLGİ VE FOTOĞRAF FORMU</h1>
          <p className="mt-4 text-sm leading-relaxed text-zinc-300 sm:text-base" data-testid="af-intro">{INTRO}</p>
        </div>
      </section>
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="rounded-xl bg-white p-6 shadow-sm sm:p-8"><Body /></div>
      </div>
    </div>
  );
}
